import { ReactNode, useCallback, useSyncExternalStore } from 'react';
import { AnalyticsPixelIds } from 'core/analytics/entities';
import PageLayout from 'core/common/components/PageLayout';
import { Providers } from 'core/common/components/Providers';
import { useRouter } from 'core/common/hooks';
import { funnelFeatureDefaultValue } from 'core/feature-flags/config/funnelFeature';
import { FeatureFlagsConfig } from 'core/feature-flags/entities';
import { resolveFlowId, DEFAULT_FLOW } from 'core/feature-flags/utils';
import { Flow } from 'core/flow/entities';
import { FunnelConfig } from 'core/funnel/entities';
import { getFunnelConfig } from 'core/funnel/store/selectors';
import { getIsRedesign } from 'core/funnel/utils';
import { useApp } from './useApp';

type AppProps = {
  children: ReactNode;
  analyticsPixelIds: AnalyticsPixelIds;
  screenId: string;
  funnelId: string;
  funnelConfig: FunnelConfig | null;
  features: FeatureFlagsConfig['features'];
  isDraftRedesignFlow: boolean;
};

const getActiveFlow = (
  funnelConfig: FunnelConfig | null,
  flowId: string,
  isDraftFlow: boolean,
): Flow | null => {
  if (!funnelConfig) {
    return null;
  }

  const { flows } = funnelConfig;

  // the draft page is served with the single requested flow
  if (isDraftFlow) {
    return flows[0] ?? null;
  }

  // mirrors the flow pick in FunnelProviderDeprecated: exact name → default_flow → first flow
  const activeFlow = flows.find(({ name }) => name === flowId);
  const defaultFlow = flows.find(({ name }) => name === DEFAULT_FLOW);

  return activeFlow ?? defaultFlow ?? flows[0] ?? null;
};

const App = ({
  children,
  analyticsPixelIds,
  funnelId,
  funnelConfig,
  screenId,
  features,
  isDraftRedesignFlow = false,
}: AppProps) => {
  const { config, appStore, services, featureFlags, theme } = useApp({
    analyticsPixelIds,
    features,
  });

  const { locale = 'en' } = useRouter();

  const resolveActiveFlowId = useCallback(() => {
    const funnel = featureFlags.getFeatureValue(funnelId, funnelFeatureDefaultValue);
    return resolveFlowId(funnel, locale);
  }, [featureFlags, funnelId, locale]);

  const subscribeToFlowSources = useCallback(
    (onChange: () => void) => {
      const unsubscribeStore = appStore.subscribe(onChange);
      const unsubscribeFeatureFlags = featureFlags.setStateUpdateHandler(onChange);

      return () => {
        unsubscribeStore();
        if (unsubscribeFeatureFlags) {
          unsubscribeFeatureFlags();
        }
      };
    },
    [appStore, featureFlags],
  );

  const flowId = useSyncExternalStore(
    subscribeToFlowSources,
    resolveActiveFlowId,
    resolveActiveFlowId,
  );

  const storeFunnelConfig = useSyncExternalStore(
    appStore.subscribe,
    () => getFunnelConfig(appStore.getState()).config,
    () => null,
  );

  const isRedesignFlow = getIsRedesign(flowId);

  if (isRedesignFlow || isDraftRedesignFlow) {
    const activeFlow = getActiveFlow(
      funnelConfig ?? storeFunnelConfig,
      flowId,
      isDraftRedesignFlow,
    );

    return (
      <Providers
        config={config}
        store={appStore}
        featureFlags={featureFlags}
        services={services}
        themeConfig={activeFlow?.appearance?.themeConfig ?? null}
      >
        {children}
      </Providers>
    );
  }

  return (
    <PageLayout
      config={config}
      store={appStore}
      featureFlags={featureFlags}
      services={services}
      theme={theme}
      screenId={screenId}
    >
      {children}
    </PageLayout>
  );
};

export default App;
